<template>
  <Widget
    widgetName="top-category-usage"
    :customParams="customParams"
    @dataFetched="handleDataFetched"
    @timeOptionChange="handleTimeOptionChange"
    @locationOptionChange="handleLocationOptionChange"
  >
    <template v-slot:content>
      <div class="px-6">
        <p
          v-if="selectedCategoryId"
          class="ma-0 pa-0 blue-grey--text text--darken-2 font-size-12 font-weight-400 mb-3"
          style="line-height: 16px; letter-spacing: 0.4px;"
        >
          <span class="font-weight-600"
            >'{{ $t(`common.categories.${topCategories[0].category}`) }}'
          </span>
          {{ $t('insights.top_category_subtitle') }}
        </p>
        <div v-else class="mb-3">
          <v-skeleton-loader
            type="text"
            width="100%"
            class="skeleton-category-title"
          />
          <v-skeleton-loader
            type="text"
            width="50%"
            class="skeleton-category-title"
          />
        </div>
        <div class="d-flex flex-wrap">
          <div
            class="top-categories-table-container"
            :style="
              $vuetify.breakpoint.mdAndDown ? 'width: 100%' : 'width: 52%'
            "
          >
            <v-data-table
              class="rounded category-table"
              disable-sort
              mobile-breakpoint="0"
              disable-filtering
              disable-pagination
              hide-default-footer
              :headers="headers"
              :items="items"
              :items-per-page="5"
            />
          </div>
          <div
            class="d-flex flex-column"
            :class="$vuetify.breakpoint.mdAndDown ? 'mt-2' : 'ms-6'"
          >
            <div>
              <Menu
                closeOnContentClick
                :options="categoriesMenu"
                :selectedOption="selectedCategoryId"
                @click="setCategory"
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    outlined
                    small
                    class="d-flex align-center justify-center rounded-bordered-button"
                    color="blue-grey lighten-3"
                    :ripple="false"
                    :disabled="loadingCategories"
                  >
                    <div class="d-flex" v-if="!loadingCategories">
                      <p
                        style="line-height: 16px; letter-spacing: 0.4px; max-width: 171px;"
                        class="text-truncate ma-0 pa-0 blue-grey--text text--darken-1 font-size-12 font-weight-400"
                      >
                        {{ currentCategory?.title }}
                      </p>
                      <v-icon size="16" color="blue-grey darken-1" class="ms-2"
                        >mdi-chevron-down</v-icon
                      >
                    </div>
                    <div v-else class="d-flex align-center justify-center">
                      <v-progress-circular
                        indeterminate
                        color="blue-grey darken-1"
                        :size="10"
                        :width="1"
                      />
                    </div>
                  </v-btn>
                </template>
              </Menu>
            </div>
            <div v-if="loadingCategory || loadingCategories">
              <TrendSkeleton class="my-2" />
              <TrendSkeleton />
            </div>
            <div
              v-else
              class="d-flex m-3"
              :class="
                $vuetify.breakpoint.mdAndDown
                  ? 'justify-space-between'
                  : 'flex-column'
              "
            >
              <Trend
                :class="$vuetify.breakpoint.mdAndDown ? 'me-4' : 'mb-3'"
                :title="$t('insights.in_rev_other')"
                :total="revsTotal"
                :percentageChange="revsPercentageChange"
                :totalLastPeriod="revsTotalLastPeriod"
              />
              <Trend
                :title="$t('insights.in_finding_other')"
                :total="routinesTotal"
                :percentageChange="routinesPercentageChange"
                :totalLastPeriod="routinesTotalLastPeriod"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </Widget>
</template>

<script>
import Widget from '../SharedComponents/WidgetBase.vue'
import Menu from '@/components/UI/Menu.vue'
import Trend from './Trend.vue'
import TrendSkeleton from './TrendSkeleton.vue'

import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import api from '@/api'
import { mapGetters } from 'vuex'

export default {
  name: 'LeadingCategories',

  mixins: [mixin, trackerMixin],

  components: {
    Menu,
    Widget,
    Trend,
    TrendSkeleton,
  },

  data() {
    return {
      categories: [],
      topCategories: [],
      loadingCategories: false,
      currentCategory: {},
      customParams: { limit: 5 },
      selectedCategoryId: null,
      loadingCategory: true,
      selectedSite: null,
      selectedTimeOption: 'l30days',
      categoryInsight: {},
    }
  },

  computed: {
    ...mapGetters(['organizationId']),

    headers() {
      return [
        {
          text: this.$t('common.category_one'),
          align: 'start',
          sortable: false,
          value: 'name',
          class:
            'blue-grey--text text--darken-1 font-size-10 font-weight-600 px-2 py-0 ma-0 top-categories-header',
          cellClass:
            'blue-grey--text text--darken-3 font-size-10 font-weight-500 ps-2 ma-0 pa-0 top-categories-names py-1',
        },
        {
          text: this.$t('common.total'),
          align: 'start',
          sortable: false,
          value: 'total',
          class:
            'blue-grey--text text--darken-1 font-size-10 font-weight-600 px-2 py-0 ma-0 top-categories-header',
          cellClass:
            'blue-grey--text text--darken-3 font-size-10 font-weight-500 ps-2 ma-0 pa-0',
        },
        {
          text: this.$t('insights.percentage'),
          align: 'start',
          sortable: false,
          value: 'percentageText',
          class:
            'blue-grey--text text--darken-1 font-size-10 font-weight-600 px-2 py-0 ma-0 top-categories-header',
          cellClass:
            'blue-grey--text text--darken-3 font-size-10 font-weight-500 ps-2 ma-0 pa-0',
        },
      ]
    },

    revsTotal() {
      return {
        text: this.formatNumber(this.categoryInsight?.data?.revs?.total),
        value: Number(this.categoryInsight?.data?.revs?.total ?? 0),
      }
    },

    revsTotalLastPeriod() {
      return Number(this.categoryInsight?.data?.revs?.totalLastPeriod ?? 0)
    },

    revsPercentageChange() {
      return Number(this.categoryInsight?.data?.revs?.percentageChange ?? 0)
    },

    routinesTotal() {
      return {
        text: this.formatNumber(this.categoryInsight?.data?.findings?.total),
        value: Number(this.categoryInsight?.data?.findings?.total ?? 0),
      }
    },

    routinesTotalLastPeriod() {
      return Number(this.categoryInsight?.data?.findings?.totalLastPeriod ?? 0)
    },

    routinesPercentageChange() {
      return Number(this.categoryInsight?.data?.findings?.percentageChange ?? 0)
    },

    items() {
      return this.topCategories.map(item => ({
        ...item,
        name: this.$t(`common.categories.${item.category}`),
        percentageText: `${item.percentageChange ? item.percentageChange : 0}%`,
      }))
    },

    categoriesMenu() {
      return this.categories.map(category => ({
        id: category.name,
        title: this.$t(`common.categories.${category.name}`),
      }))
    },
  },

  methods: {
    handleTimeOptionChange(option) {
      this.selectedTimeOption = option
      this.getCategoryWidget()
    },

    handleLocationOptionChange(option) {
      this.selectedSite = option
      this.getCategoryWidget()
    },

    setCategory(category) {
      this.trackInsightsWidgetNavigate({
        widget: 'top-category-usage',
        action: 'selectCategory',
        category,
      })
      this.selectedCategoryId = category.id
      this.currentCategory = { ...category }
      this.getCategoryWidget()
    },

    async getCategoryWidget() {
      try {
        if (!this.selectedCategoryId) return
        this.loadingCategory = true

        const params = {
          organizationId: this.organizationId,
          insight: 'category-usage',
          dateRange: this.selectedTimeOption,
          category: this.selectedCategoryId,
        }

        if (this.selectedSite) params.site = this.selectedSite

        const response = await api.insights.getInsight(params)

        this.categoryInsight = response.data
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loadingCategory = false
      }
    },

    async handleDataFetched(widget) {
      this.topCategories = [...widget.data]
      await this.fetchCategories()

      const topOne = this.topCategories?.[0] ?? {}

      this.setCategory({
        title: this.$t(`common.categories.${topOne.category}`),
        id: topOne.category,
      })
    },

    async fetchCategories() {
      try {
        this.loadingCategories = true

        const response = await api.category.getOrganizationCatalog(
          this.organizationId
        )

        this.categories = response.data
      } catch (err) {
        this.showError(err.message)
      } finally {
        this.loadingCategories = false
      }
    },

    formatNumber(value) {
      const abreviatedValue = Intl.NumberFormat('en-us', {
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(value)
      return abreviatedValue
    },
  },
}
</script>
<style lang="scss">
.top-categories-names {
  line-height: 10px;
}

.skeleton-category-title {
  .v-skeleton-loader__text {
    height: 10px;
  }
}
.top-categories-table-container {
  .category-table {
    border: 1px solid $color-blue-grey-lighten2;
  }

  .top-categories-header {
    height: 23px !important;
  }

  .v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
  .v-data-table > .v-data-table__wrapper > table > thead > tr > td,
  .v-data-table > .v-data-table__wrapper > table > tfoot > tr > td {
    height: 23px !important;
  }
}
.rounded-bordered-button {
  border-radius: 4px !important;
  border: 1px solid $color-blue-grey-lighten1 !important;
  &:hover {
    background-color: $color-blue-grey-lighten3 !important;
    &::before {
      background-color: $color-blue-grey-lighten3 !important;
      opacity: 1 !important;
    }
  }
}
</style>
